import { AccountApi } from '@/api/account.api';
import { NaluriRefreshTokenError } from '@/errors';
import { tokenStore } from '@/utils/tokenStore';
import version from './version';

export class ApiClient {
  constructor(
    private readonly accountApi: AccountApi,
    private readonly onRefreshTokenError?: () => Promise<void>
  ) {}

  public async fetch(url: string, options: RequestInit): Promise<Response> {
    return await this._fetch(url, {
      headers: this.getDefaultHeaders(),
      ...options,
    });
  }

  private async _fetch(
    url: string,
    options: RequestInit,
    retried = false
  ): Promise<Response> {
    const accessToken = tokenStore.getItem('accessToken');

    if (accessToken) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
      };
    }

    const response = await fetch(url, options);

    if (response.status === 401 && retried) {
      if (this.onRefreshTokenError) {
        await this.onRefreshTokenError();

        // @ts-expect-error refresh token error
        return {
          ok: false,
          json: () => ({
            message: 'Refresh flow retried but failed',
            statusCode: 8888,
          }),
          status: 401,
        } as Response;
      }

      throw new NaluriRefreshTokenError();
    }

    if (response.status === 401) {
      const refreshToken = tokenStore.getItem('refreshToken');

      if (!refreshToken) {
        if (this.onRefreshTokenError) {
          await this.onRefreshTokenError();
          // @ts-expect-error refresh token error
          return {
            ok: false,
            json: () => ({
              message: 'Missing refresh token',
              statusCode: 8888,
            }),
            status: 401,
          } as Response;
        }

        throw new NaluriRefreshTokenError();
      }

      try {
        const { accessToken } = await this.accountApi.refresh(refreshToken);

        tokenStore.setItem('accessToken', accessToken);
      } catch (error) {
        if (this.onRefreshTokenError) {
          await this.onRefreshTokenError();
          // @ts-expect-error refresh token error
          return {
            ok: false,
            json: () => ({
              statusCode: 8888,
              message: `Refresh API failed: ${
                (error as Error).message || error
              }`,
            }),
            status: 401,
          } as Response;
        }
        throw new NaluriRefreshTokenError();
      }

      return this._fetch(url, options, true);
    }

    return response;
  }

  private getDefaultHeaders() {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'User-Agent': `Naluri Chat SDK /JS (${version}})`,
      'x-nl-sdk-version': version,
    };
  }
}
